import { useState, useCallback, useEffect, Children, isValidElement, cloneElement } from 'react'
import ContextMenu from './ContextMenu'

const RECENT_APPS_STORAGE_KEY = 'pp_recent_apps'

const ContextMenuProvider = ({ children, onDesktopAction, onWindowAction, onTaskbarAction, windows, pinnedApps = [], onPersonalize, recentAppsLimit = 5, theme }) => {
  const [contextMenu, setContextMenu] = useState(null)
  const [recentApps, setRecentApps] = useState([])

  // Load recent apps from localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem(RECENT_APPS_STORAGE_KEY)
      if (stored) {
        setRecentApps(JSON.parse(stored))
      }
    } catch (_) {
      /* ignore */
    }
  }, [])

  const persistRecentApps = (list) => {
    try {
      localStorage.setItem(RECENT_APPS_STORAGE_KEY, JSON.stringify(list))
    } catch (_) {
      /* ignore */
    }
  }

  const addRecentApp = useCallback((appName) => {
    if (!appName) return
    setRecentApps(prev => {
      const next = [appName, ...prev.filter(a => a !== appName)].slice(0, recentAppsLimit)
      persistRecentApps(next)
      return next
    })
  }, [recentAppsLimit])

  // Move getContextMenuItems inside component to access current state
  const getContextMenuItems = useCallback((type, data) => {
    switch (type) {
      case 'desktop': {
        const base = [
          {
            label: 'Refresh Desktop',
            icon: 'fas fa-sync-alt',
            action: 'refresh'
          }
        ]

        // Inject recent apps if any
        if (recentApps.length) {
          base.push(
            { type: 'separator' },
            { type: 'header', label: 'Recent Apps' },
            ...recentApps.map(app => ({
              label: app,
              action: 'open',
              icon: 'fas fa-clock',
              data: { appName: app }
            })),
            { label: 'Clear Recent Apps', icon: 'fas fa-trash', action: 'clear-recent', disabled: false }
          )
        }

        // Submenu: New > Folder/File (future expandable)
        base.push(
          { type: 'separator' },
          {
            label: 'New',
            icon: 'fas fa-plus',
            submenu: [
              {
                label: 'Folder',
                icon: 'fas fa-folder-plus',
                action: 'new-folder',
                disabled: true
              },
              {
                label: 'File',
                icon: 'fas fa-file-plus',
                action: 'new-file',
                disabled: true
              }
            ]
          },
          { type: 'separator' },
          {
            label: 'Personalize',
            icon: 'fas fa-palette',
            action: 'personalize'
          },
          {
            label: 'Display Settings',
            icon: 'fas fa-desktop',
            action: 'display-settings'
          },
          { type: 'separator' },
          {
            label: 'Properties',
            icon: 'fas fa-info-circle',
            action: 'properties'
          }
        )
        return base
      }

      case 'window':
        return [
          {
            label: data.window?.isMaximized ? 'Restore' : 'Maximize',
            icon: data.window?.isMaximized ? 'fas fa-window-restore' : 'fas fa-window-maximize',
            action: 'maximize',
            disabled: false
          },
          {
            label: data.window?.isMinimized ? 'Restore' : 'Minimize',
            icon: 'fas fa-window-minimize',
            action: 'minimize',
            disabled: false
          },
          { type: 'separator' },
          {
            label: 'Move',
            icon: 'fas fa-arrows-alt',
            action: 'move',
            disabled: data.window?.isMaximized
          },
          {
            label: 'Size',
            icon: 'fas fa-expand-arrows-alt',
            action: 'size',
            disabled: data.window?.isMaximized
          },
          { type: 'separator' },
          {
            label: 'Close',
            icon: 'fas fa-times',
            action: 'close',
            shortcut: 'Alt+F4'
          }
        ]

      case 'taskbar': {
        const isOpen = windows.some(w => w.appName === data.appName)
        const isPinned = pinnedApps.includes(data.appName)
        return [
          {
            label: isOpen ? 'Close Window' : 'Open',
            icon: isOpen ? 'fas fa-times' : 'fas fa-external-link-alt',
            action: isOpen ? 'close' : 'open'
          },
            { type: 'separator' },
          {
            label: isPinned ? 'Unpin from Taskbar' : 'Pin to Taskbar',
            icon: 'fas fa-thumbtack',
            action: isPinned ? 'unpin' : 'pin',
            disabled: false
          }
        ]
      }

      case 'desktop-icon': {
        const isIconPinned = pinnedApps.includes(data.appName)
        return [
          {
            label: 'Open',
            icon: 'fas fa-external-link-alt',
            action: 'open'
          },
          { type: 'separator' },
          {
            label: isIconPinned ? 'Unpin from Taskbar' : 'Pin to Taskbar',
            icon: 'fas fa-thumbtack',
            action: isIconPinned ? 'unpin' : 'pin',
            disabled: false
          },
          { type: 'separator' },
          {
            label: 'Properties',
            icon: 'fas fa-info-circle',
            action: 'icon-properties',
            disabled: true
          }
        ]
      }

      case 'start-menu-icon': {
        const isStartMenuPinned = pinnedApps.includes(data.appName)
        return [
          {
            label: 'Open',
            icon: 'fas fa-external-link-alt',
            action: 'open'
          },
          { type: 'separator' },
          {
            label: isStartMenuPinned ? 'Unpin from Taskbar' : 'Pin to Taskbar',
            icon: 'fas fa-thumbtack',
            action: isStartMenuPinned ? 'unpin' : 'pin',
            disabled: false
          }
        ]
      }

      default:
        return []
    }
  }, [pinnedApps, windows, recentApps])

  const showContextMenu = useCallback((event, type, data = {}) => {
    event.preventDefault()
    event.stopPropagation()
    const position = { x: event.clientX, y: event.clientY }
    const items = getContextMenuItems(type, data)
    setContextMenu({ position, items, type, data })
  }, [getContextMenuItems])

  // Listen for custom context menu events
  useEffect(() => {
    const handleCustomContextMenu = (event) => {
      const { event: originalEvent, type, data } = event.detail
      showContextMenu(originalEvent, type, data)
    }
    document.addEventListener('showContextMenu', handleCustomContextMenu)
    return () => {
      document.removeEventListener('showContextMenu', handleCustomContextMenu)
    }
  }, [showContextMenu])

  // Close context menu when pinnedApps changes to refresh menu items
  useEffect(() => {
    if (contextMenu) {
      setContextMenu(null)
    }
  }, [pinnedApps])

  const hideContextMenu = useCallback(() => {
    setContextMenu(null)
  }, [])

  const handleItemClick = (item) => {
    const { type, data: ctxData } = contextMenu
    const data = item.data || ctxData || {}

    // Record recent app if appropriate
    if (item.action === 'open' && data.appName) {
      addRecentApp(data.appName)
    }
    if (item.action === 'clear-recent') {
      setRecentApps([])
      persistRecentApps([])
      hideContextMenu()
      return
    }
    if (item.action === 'personalize' || item.action === 'open-personalize') {
      onPersonalize?.(item.data?.section)
      try { window.dispatchEvent(new CustomEvent('openPersonalize', { detail: item.data })) } catch(_){}
    }
    if (item.action === 'wallpaper-gradient') {
      const val = item.data?.value
      if (val) {
        try { window.dispatchEvent(new CustomEvent('wallpaperChange', { detail: { kind: 'gradient', value: val } })) } catch(_){}
        localStorage.setItem('pp_wallpaper_kind', 'gradient')
        localStorage.setItem('pp_wallpaper_value', val)
      }
    }
    if (item.action === 'icon-theme') {
      const id = item.data?.id
      if (id) {
        try { window.dispatchEvent(new CustomEvent('iconThemeChange', { detail: { id } })) } catch(_){}
        localStorage.setItem('pp_icon_theme', id)
      }
    }

    switch (type) {
      case 'desktop':
        onDesktopAction?.(item.action, data)
        break
      case 'window':
        onWindowAction?.(item.action, data)
        break
      case 'taskbar':
      case 'desktop-icon':
      case 'start-menu-icon':
        onTaskbarAction?.(item.action, data)
        break
    }
    
    // Close the context menu after handling the action
    hideContextMenu()
  }

  // Add context menu event listeners to children (restored after truncation)
  const addContextMenuToChildren = (children) => {
    return Children.map(children, child => {
      if (!isValidElement(child)) return child
      if (child.props?.disableContextMenu) return child
      const processedChildren = child.props?.children ? addContextMenuToChildren(child.props.children) : child.props.children
      const type = child.props?.contextMenuType
      const data = child.props?.contextMenuData || {}
      if (!type) {
        if (processedChildren !== child.props.children) {
          return cloneElement(child, {}, processedChildren)
        }
        return child
      }
      const existingHandler = child.props.onContextMenu
      const handle = (e) => {
        existingHandler?.(e)
        if (!e.defaultPrevented) {
          showContextMenu(e, type, data)
        }
      }
      return cloneElement(child, { onContextMenu: handle }, processedChildren)
    })
  }

  return (
    <>
      {addContextMenuToChildren(children)}
      {contextMenu && (
        <ContextMenu
          items={contextMenu.items}
          position={contextMenu.position}
          onClose={hideContextMenu}
          onItemClick={handleItemClick}
          theme={theme}
        />
      )}
    </>
  )
}

export default ContextMenuProvider
